import React, { useState, useEffect, useContext } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { ArrowRight, CheckCircle2, ShieldPlus, CheckCheck, FileSearch } from 'lucide-react'; // FileSearch: ReviewDetailModal에서 사용
import { useNavigate } from 'react-router-dom';
import { useEvalResults, useEvalResult, useEvalResultDetail, useReviews, useReviewDetail, useReviewMutations, useBatches } from '../../../hooks/console/useConsoleAPI';
import Pagination from '../shared/Pagination';
import { CONSOLE_VERDICT_STYLE, getVBg, getVText, getScoreColor } from '../../../constants/consoleConstants';
import { AuthContext } from '../../../../../../context/AuthContext';
import { toast } from 'react-toastify';

const REVIEW_PER = 5;
const BATCH_PER  = 6; // 2행 × 3열

const ISSUE_TYPES = [
    { key: 'retrieval',   label: '검색/조문 누락' },
    { key: 'target',      label: '대상 오류' },
    { key: 'penalty',     label: '수치/벌칙 누락' },
    { key: 'fatal_error', label: '치명 오류 (금지어)' },
    { key: 'other',       label: '기타' },
];

const STATUS_META = {
    OPEN:     { label: '검토 대기', color: 'text-amber-600',   bg: 'bg-amber-100' },
    REVIEWED: { label: '검토 완료', color: 'text-blue-500',    bg: 'bg-blue-100' },
    CLOSED:   { label: '처리 완료', color: 'text-emerald-500', bg: 'bg-emerald-100' },
};

const BATCH_STATUS_META = {
    RUNNING:   { label: '실행 중', color: 'text-blue-600',    bg: 'bg-blue-100',    dot: 'bg-blue-500' },
    COMPLETED: { label: '완료',    color: 'text-emerald-600', bg: 'bg-emerald-100', dot: 'bg-emerald-500' },
    FAILED:    { label: '실패',    color: 'text-red-500',     bg: 'bg-red-100',     dot: 'bg-red-500' },
    PENDING:   { label: '대기 중', color: 'text-gray-500',    bg: 'bg-gray-100',    dot: 'bg-gray-400' },
};

/* ── 검토 상세 모달 ── */
function ReviewDetailModal({ reviewId, evalItem, onClose, onComplete, onRegisterPolicy }) {
    // GET /review/{id} — review_comment, suggested_policy_text 포함 전체 필드
    const { data: reviewDetail, isLoading: reviewLoading } = useReviewDetail(reviewId);
    const evalId = reviewDetail?.evaluation_result_id;
    // 평가 결과 (answer_text, standard_answer, grade, score 등)
    const { data: evalResult, isLoading: resultLoading } = useEvalResult(evalId);

    const review = reviewDetail ?? {};
    const st = STATUS_META[review.review_status] ?? STATUS_META.OPEN;
    const issueLabel = ISSUE_TYPES.find((t) => t.key === review.issue_type)?.label ?? review.issue_type;

    const base = { ...(evalResult ?? {}), ...(evalItem ?? {}) };
    const d    = {};

    return (
        <Modal show onHide={onClose} centered size="lg" contentClassName="rounded-2xl border-0 shadow-xl">
            <Modal.Header className="border-b border-gray-100 px-6 py-3">
                <Modal.Title className="text-base font-bold text-sb-header flex items-center gap-2">
                    <FileSearch size={15} className="text-sb-primary" />
                    검토 상세
                    {reviewLoading && <span className="text-sm text-gray-400 font-normal">불러오는 중...</span>}
                </Modal.Title>
                <button type="button" className="modal-btn-close" onClick={onClose}>
                    <span className="modal-btn-span">X</span>
                </button>
            </Modal.Header>

            <Modal.Body className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">

                {/* ① 평가 결과 */}
                <div>
                    <p className="text-sm uppercase tracking-wider text-gray-400 mb-2">평가 결과</p>
                    <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 space-y-3">
                        {/* 질문 + 점수 */}
                        <div className="flex items-start gap-3">
                            <div className="flex-1 space-y-1">
                                <p className="text-sm text-gray-400">질문</p>
                                <p className="text-base font-medium text-sb-header">
                                    {base.question ?? '—'}
                                </p>
                            </div>
                            <div className="text-right shrink-0">
                                <span className={`text-2xl font-bold ${getScoreColor(base.final_score ?? 0)}`}>
                                    {base.final_score ?? '—'}
                                </span>
                                <span className="text-base text-gray-400">점</span>
                                <div className="mt-0.5">
                                    <span className={`text-sm px-2 py-0.5 rounded-full font-medium ${getVBg(base.grade)} ${getVText(base.grade)}`}>
                                        {base.grade ?? '—'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* AI 답변 */}
                        <div>
                            <p className="text-sm text-gray-400 mb-1">AI 답변</p>
                            {resultLoading ? (
                                <p className="text-base text-gray-400">불러오는 중...</p>
                            ) : (
                                <p className="text-base text-gray-700 whitespace-pre-wrap leading-relaxed bg-white rounded-lg p-3 border border-gray-100 min-h-[3rem]">
                                    {base.answer_text ?? d.answer_text ?? '—'}
                                </p>
                            )}
                        </div>

                        {/* 정답 */}
                        <div>
                            <p className="text-sm text-gray-400 mb-1">등록된 정답</p>
                            {resultLoading ? (
                                <p className="text-base text-gray-400">불러오는 중...</p>
                            ) : (
                                <p className="text-base text-gray-600 whitespace-pre-wrap leading-relaxed bg-white rounded-lg p-3 border border-gray-100 min-h-[3rem]">
                                    {base.standard_answer ?? d.standard_answer ?? '—'}
                                </p>
                            )}
                        </div>

                        {/* 점수 세부 (있을 때만) */}
                        {(base.rule_score != null || base.llm_score != null) && (
                            <div className="grid grid-cols-2 gap-2">
                                {base.rule_score != null && (
                                    <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-white border border-gray-100">
                                        <span className="text-sm text-gray-500">규칙 기반 점수</span>
                                        <span className={`text-base font-bold ${getScoreColor(base.rule_score)}`}>{base.rule_score}</span>
                                    </div>
                                )}
                                {base.llm_score != null && (
                                    <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-white border border-gray-100">
                                        <span className="text-sm text-gray-500">AI 판단 점수</span>
                                        <span className={`text-base font-bold ${getScoreColor(base.llm_score)}`}>{base.llm_score}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* AI 판단 이유 */}
                        {base.llm_eval_reason && (
                            <div>
                                <p className="text-sm text-gray-400 mb-1">AI 판단 이유</p>
                                <p className="text-base text-gray-600 italic bg-white rounded-lg p-3 border border-gray-100">{base.llm_eval_reason}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* ② 검토 내용 */}
                <div>
                    <p className="text-sm uppercase tracking-wider text-gray-400 mb-2">검토 내용</p>
                    <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 space-y-3">
                        {/* 상태 + 유형 + 날짜 */}
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-sm px-2 py-0.5 rounded-full font-medium ${st.bg} ${st.color}`}>{st.label}</span>
                            <span className="text-sm px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">{issueLabel}</span>
                            {review.action_required === 'Y' && (
                                <span className="text-sm px-2 py-0.5 rounded-full bg-red-100 text-red-600">조치 필요</span>
                            )}
                            <span className="ml-auto text-sm text-gray-400">{review.created_at?.slice(0, 16).replace('T', ' ')}</span>
                        </div>

                        {/* 문제 요약 */}
                        <div>
                            <p className="text-sm text-gray-400 mb-0.5">문제 요약</p>
                            <p className="text-base font-medium text-sb-header">{review.problem_summary || '—'}</p>
                        </div>

                        {/* 상세 검토 의견 */}
                        <div>
                            <p className="text-sm text-gray-400 mb-0.5">상세 검토 의견</p>
                            <p className="text-base text-gray-700 whitespace-pre-wrap bg-white rounded-lg p-3 border border-gray-100 min-h-[2.5rem]">
                                {review.review_comment || '—'}
                            </p>
                        </div>

                        {/* 개선 방향 */}
                        <div>
                            <p className="text-sm text-gray-400 mb-0.5">개선 방향</p>
                            {review.suggested_policy_text ? (
                                <div className="flex items-start gap-2 p-2.5 rounded-lg bg-blue-50 border border-blue-200">
                                    <ArrowRight size={11} className="text-blue-500 mt-0.5 shrink-0" />
                                    <p className="text-base text-blue-700 flex-1">{review.suggested_policy_text}</p>
                                </div>
                            ) : (
                                <p className="text-base text-gray-400">—</p>
                            )}
                        </div>

                        {/* 검토자 */}
                        <p className="text-sm text-gray-400 pt-2 border-t border-gray-200">
                            검토자: <span className="text-gray-600">{review.reviewer_id || '—'}</span>
                        </p>
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer className="border-t border-gray-100 px-6 py-3 gap-2">
                {review.suggested_policy_text && (
                    <Button
                        variant="outline-primary"
                        className="text-base flex items-center gap-1"
                        onClick={() => { onRegisterPolicy(review); onClose(); }}
                    >
                        <ShieldPlus size={12} /> 규칙으로 등록
                    </Button>
                )}
                {review.review_status !== 'CLOSED' && (
                    <Button
                        variant="success"
                        className="text-base flex items-center gap-1"
                        onClick={() => onComplete(review)}
                    >
                        <CheckCircle2 size={12} /> 완료 처리
                    </Button>
                )}
                <Button variant="light" className="text-base text-gray-600 ms-auto" onClick={onClose}>닫기</Button>
            </Modal.Footer>
        </Modal>
    );
}

/* ── 배치 카드 ── */
function BatchCard({ batch, selected, onClick }) {
    const meta  = BATCH_STATUS_META[batch.status] ?? BATCH_STATUS_META.PENDING;
    const date  = batch.started_at ? batch.started_at.slice(0, 16).replace('T', ' ') : '—';
    const name  = batch.batch_name || `실행 #${batch.id}`;

    const failCnt = batch.fail_count ?? null;
    const warnCnt = batch.warn_count ?? null;
    const passCnt = batch.pass_count ?? null;
    const total   = batch.total_count ?? null;

    return (
        <button
            onClick={onClick}
            className={`w-full text-left p-3 rounded-xl border-2 transition-all
                ${selected
                    ? 'border-sb-primary bg-blue-50 shadow-md'
                    : 'border-gray-100 bg-white hover:border-gray-300 hover:shadow-sm'}`}
        >
            {/* 상단: 이름 + 상태 */}
            <div className="flex items-start justify-between gap-1 mb-1.5">
                <span className={`text-xs font-bold leading-tight ${selected ? 'text-sb-primary' : 'text-sb-header'} truncate flex-1`}>
                    {name}
                </span>
                {selected && <CheckCheck size={13} className="text-sb-primary shrink-0 mt-0.5" />}
            </div>

            {/* 상태 뱃지 + 버전 */}
            <div className="flex items-center gap-1.5 mb-2">
                <span className={`flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded-full ${meta.bg} ${meta.color}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                    {meta.label}
                </span>
                {batch.engine_version && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-mono">
                        v{batch.engine_version}
                    </span>
                )}
            </div>

            {/* 날짜 */}
            <p className="text-sm text-gray-400 mb-2">{date}</p>

            {/* 점수 분포 (있을 때만) */}
            {(failCnt !== null || warnCnt !== null || passCnt !== null) && (
                <div className="flex items-center gap-2 text-sm">
                    {passCnt !== null && (
                        <span className="text-emerald-600 font-semibold">정상 {passCnt}</span>
                    )}
                    {warnCnt !== null && (
                        <span className="text-amber-500 font-semibold">주의 {warnCnt}</span>
                    )}
                    {failCnt !== null && (
                        <span className="text-red-500 font-semibold">개선필요 {failCnt}</span>
                    )}
                    {total !== null && (
                        <span className="text-gray-400 ml-auto">/ {total}건</span>
                    )}
                </div>
            )}

            {/* 판정분포 미니 바 (있을 때만) */}
            {total > 0 && (failCnt !== null || warnCnt !== null || passCnt !== null) && (
                <div className="flex h-1.5 rounded-full overflow-hidden mt-1.5">
                    {passCnt > 0 && (
                        <div className="bg-emerald-400 h-full" style={{ flex: passCnt }} />
                    )}
                    {warnCnt > 0 && (
                        <div className="bg-amber-400 h-full" style={{ flex: warnCnt }} />
                    )}
                    {failCnt > 0 && (
                        <div className="bg-red-400 h-full" style={{ flex: failCnt }} />
                    )}
                </div>
            )}
        </button>
    );
}

export default function ReviewTab() {
    const { auth } = useContext(AuthContext);
    const [selectedBatchId, setSelectedBatchId] = useState('');
    const [batchPage, setBatchPage]             = useState(1);
    const [reviewParams, setReviewParams]       = useState({ page: 1, size: REVIEW_PER });
    const [filterStatus, setFilterStatus]       = useState('all');
    const [filterIssueType, setFilterIssueType] = useState('');
    const [showForm, setShowForm]               = useState(false);
    const [editReview, setEditReview]           = useState(null);
    const [detailReview, setDetailReview]       = useState(null);
    const [completerId, setCompleterId]         = useState(auth?.userId ?? '');

    /* 배치 목록 */
    const { data: batchData } = useBatches({ page: batchPage, size: BATCH_PER });
    const batches      = batchData?.items ?? [];
    const batchTotal   = batchData?.total ?? 0;
    const batchPages   = Math.ceil(batchTotal / BATCH_PER);
    const selectedBatch = batches.find((b) => String(b.id) === selectedBatchId) ?? null;

    /* 첫 로드 시 가장 최근 배치 자동 선택 */
    useEffect(() => {
        if (batches.length > 0 && !selectedBatchId) {
            setSelectedBatchId(String(batches[0].id));
        }
    }, [batches]);

    /* 배치 변경 시 검토이력 페이지 초기화 */
    useEffect(() => {
        setReviewParams((p) => ({ ...p, page: 1 }));
    }, [selectedBatchId]);

    useEffect(() => {
        if (auth?.userId) setCompleterId(auth.userId);
    }, [auth?.userId]);

    /* 선택 배치의 WARN / FAIL 결과 — 500건까지 */
    const evalParams = selectedBatchId
        ? { batch_id: selectedBatchId, grade: 'FAIL', size: 500 }
        : { grade: 'FAIL', size: 500 };
    const warnParams = selectedBatchId
        ? { batch_id: selectedBatchId, grade: 'WARN', size: 500 }
        : { grade: 'WARN', size: 500 };

    const { data: evalData } = useEvalResults(evalParams);
    const { data: warnData } = useEvalResults(warnParams);
    const needsReview  = [...(evalData?.items ?? []), ...(warnData?.items ?? [])];
    const failTotal    = evalData?.total ?? 0;
    const warnTotal    = warnData?.total ?? 0;
    const isTruncated  = (failTotal + warnTotal) > needsReview.length;

    /* 전체 검토 기록 — 배치 필터링 + 진행률 모두 이걸로 처리 */
    const { data: allReviewsData, isLoading } = useReviews({ size: 1000, page: 1 });
    const allReviews = allReviewsData?.items ?? [];

    const navigate = useNavigate();
    const { create, complete } = useReviewMutations();

    /* 선택 배치의 평가결과 ID 집합 */
    const batchEvalIds = new Set(needsReview.map((ev) => ev.id));

    /* 검토이력 — 선택 배치 + 필터 적용 후 클라이언트 페이지네이션 */
    const filteredReviews = allReviews
        .filter((r) => !selectedBatchId || batchEvalIds.has(r.evaluation_result_id))
        .filter((r) => filterStatus === 'all' || r.review_status === filterStatus)
        .filter((r) => !filterIssueType || r.issue_type === filterIssueType);

    const total      = filteredReviews.length;
    const totalPages = Math.ceil(total / REVIEW_PER);
    const reviews    = filteredReviews.slice(
        (reviewParams.page - 1) * REVIEW_PER,
        reviewParams.page * REVIEW_PER
    );

    /* 진행률 — 선택 배치 기준 전체 검토 기록으로 계산 */
    const reviewedIds   = new Set(allReviews.map((r) => r.evaluation_result_id));
    const reviewedCount = needsReview.filter((ev) => reviewedIds.has(ev.id)).length;
    const progressPct   = needsReview.length > 0
        ? Math.round((reviewedCount / needsReview.length) * 100) : 0;

    const handleRegisterPolicy = (rv) => {
        navigate('/ai/admin/console/policy', {
            state: {
                prefill: {
                    title: rv.problem_summary,
                    description: rv.suggested_policy_text,
                    policy_type: rv.issue_type === 'retrieval'   ? 'SEARCH_RULE'
                               : rv.issue_type === 'fatal_error' ? 'FORBIDDEN'
                               : 'ANSWER_FORMAT',
                },
            },
        });
    };

    const openReviewForm = (evalItem) => {
        setEditReview({
            evaluation_result_id: evalItem.id,
            question: evalItem.question,
            grade: evalItem.grade,
            final_score: evalItem.final_score,
            issue_type: 'retrieval',
            problem_summary: '',
            review_comment: '',
            suggested_policy_text: '',
            action_required: 'Y',
            reviewer_id: auth?.userId ?? '',
        });
        setShowForm(true);
    };

    const handleSave = () => {
        if (!editReview.problem_summary || !editReview.reviewer_id || !editReview.issue_type) return;
        const { question, grade, final_score, ...payload } = editReview;
        // 등록 시 목록 API가 반환하지 않는 필드를 로컬에 캐싱
        create.mutate(payload, { onSuccess: () => setShowForm(false) });
    };

    const handleComplete = (rv) => {
        if (!completerId) { toast.warn('처리자 ID를 입력하세요.'); return; }
        if (!window.confirm('검토를 완료 처리하시겠습니까?')) return;
        complete.mutate({ id: rv.id, data: { review_status: 'CLOSED', reviewer_id: completerId } });
    };

    const hasReview = (evalId) => allReviews.some((r) => r.evaluation_result_id === evalId);

    return (
        <div className="space-y-4 w-full">
            {/* 헤더 */}
            <div>
                <p className="text-lg font-bold text-sb-header">검토 관리</p>
                <p className="text-base text-gray-400">AI가 틀리거나 부족하게 답한 항목을 확인하고, 어떻게 고쳐야 할지 의견을 남깁니다.</p>
            </div>

            {/* ── 평가 실행 선택 ── */}
            <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                    <p className="text-base font-semibold text-sb-header">평가 실행 선택</p>
                    <span className="text-xs text-gray-400">총 {batchTotal}개 실행</span>
                </div>

                {/* 카드 그리드 */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {batches.map((b) => (
                        <BatchCard
                            key={b.id}
                            batch={b}
                            selected={String(b.id) === selectedBatchId}
                            onClick={() => setSelectedBatchId(String(b.id))}
                        />
                    ))}
                    {batches.length === 0 && (
                        <div className="col-span-3 text-center py-8 space-y-1">
                            <p className="text-base font-medium text-gray-500">아직 평가를 실행하지 않았습니다.</p>
                            <p className="text-sm text-gray-400"><strong>AI 평가</strong> 탭으로 이동해 먼저 평가를 실행하세요.</p>
                        </div>
                    )}
                </div>

                {/* 페이지네이션 */}
                {batchPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-3">
                        <button
                            onClick={() => setBatchPage((p) => Math.max(1, p - 1))}
                            disabled={batchPage === 1}
                            style={{ width: '1.75rem', height: '1.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '0.25rem', border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '14px', opacity: batchPage === 1 ? 0.3 : 1 }}
                        >
                            &#8249;
                        </button>
                        <span className="text-base font-semibold text-gray-700">
                            {batchPage} / {batchPages}
                        </span>
                        <button
                            onClick={() => setBatchPage((p) => Math.min(batchPages, p + 1))}
                            disabled={batchPage === batchPages}
                            style={{ width: '1.75rem', height: '1.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '0.25rem', border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '14px', opacity: batchPage === batchPages ? 0.3 : 1 }}
                        >
                            &#8250;
                        </button>
                    </div>
                )}

                {/* 선택된 배치 진행률 */}
                {selectedBatch && needsReview.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-gray-500">
                                <span className="font-semibold text-gray-700">
                                    {selectedBatch.batch_name || `실행 #${selectedBatch.id}`}
                                </span>
                                &nbsp;— 검토 진행률&nbsp;
                                <span className="font-semibold text-gray-700">{reviewedCount}/{needsReview.length}</span>건
                            </span>
                            <span className={`text-sm font-bold ${progressPct === 100 ? 'text-emerald-600' : 'text-amber-600'}`}>
                                {progressPct}%
                            </span>
                        </div>
                        <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                            <div
                                className={`h-full rounded-full transition-all ${progressPct === 100 ? 'bg-emerald-500' : 'bg-amber-400'}`}
                                style={{ width: `${progressPct}%` }}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* ── 검토 대상 목록 ── */}
            <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                    <p className="text-base font-semibold text-sb-header">
                        검토가 필요한 항목
                        <span className="ml-2 text-base font-normal text-gray-400">
                            {selectedBatch
                                ? `${selectedBatch.batch_name || `실행 #${selectedBatch.id}`} 기준`
                                : '전체 기준'}
                            &nbsp;— {needsReview.length}건
                            {isTruncated && ` / 전체 ${failTotal + warnTotal}건`}
                        </span>
                    </p>
                    {isTruncated && (
                        <span className="text-sm px-2 py-1 rounded-lg bg-amber-100 text-amber-700">
                            500건 초과 — 일부만 표시됩니다
                        </span>
                    )}
                </div>

                <div className="space-y-2">
                    {needsReview.map((ev) => {
                        const VIcon   = CONSOLE_VERDICT_STYLE[ev.grade]?.icon;
                        const reviewed = hasReview(ev.id);
                        const gradeLabel = ev.grade === 'FAIL' ? '개선 필요' : ev.grade === 'WARN' ? '주의' : ev.grade;
                        return (
                            <div key={ev.id} className={`flex items-center gap-3 p-3 rounded-lg ${reviewed ? 'bg-emerald-50' : 'bg-gray-50'}`}>
                                {VIcon && <VIcon size={15} className={getVText(ev.grade)} />}
                                <span className="flex-1 text-base text-gray-700 truncate">{ev.question}</span>
                                <span className={`text-base font-bold shrink-0 ${getScoreColor(ev.final_score)}`}>{ev.final_score}점</span>
                                <span className={`text-sm px-2 py-0.5 rounded-full shrink-0 ${getVBg(ev.grade)} ${getVText(ev.grade)}`}>{gradeLabel}</span>
                                {reviewed
                                    ? <span className="text-sm px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 shrink-0 flex items-center gap-1">
                                        <CheckCheck size={10} /> 검토 등록됨
                                      </span>
                                    : <button
                                        onClick={() => openReviewForm(ev)}
                                        className="text-sm px-2 py-1 rounded-lg bg-sb-primary text-white hover:bg-blue-700 transition-colors shrink-0"
                                      >
                                        검토하기
                                      </button>
                                }
                            </div>
                        );
                    })}
                    {needsReview.length === 0 && (
                        <p className="text-base text-gray-400 text-center py-4">
                            {selectedBatchId ? '해당 평가 실행에 검토 대상이 없습니다.' : '검토 대상이 없습니다.'}
                        </p>
                    )}
                </div>
            </div>

            {/* ── 검토 이력 ── */}
            <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-3">
                    <p className="text-base font-semibold text-sb-header">검토 이력</p>
                    <div className="flex flex-wrap gap-1.5 items-center">
                        <span className="px-2 py-1 rounded text-sm border border-gray-100 bg-gray-50 text-gray-600 w-28 text-center truncate" title={completerId}>
                            {completerId || '로그인 필요'}
                        </span>
                        {['all', 'OPEN', 'REVIEWED', 'CLOSED'].map((s) => (
                            <button
                                key={s}
                                onClick={() => { setFilterStatus(s); setReviewParams((p) => ({ ...p, page: 1 })); }}
                                className={`px-2 py-1 rounded text-sm font-medium transition-colors ${filterStatus === s ? 'bg-sb-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                            >
                                {s === 'all' ? '전체' : STATUS_META[s]?.label}
                            </button>
                        ))}
                        <select
                            value={filterIssueType}
                            onChange={(e) => { setFilterIssueType(e.target.value); setReviewParams((p) => ({ ...p, page: 1 })); }}
                            className="px-2 py-1 rounded text-sm border border-gray-200 bg-white outline-none focus:border-sb-primary"
                        >
                            <option value="">전체 유형</option>
                            {ISSUE_TYPES.map((t) => (
                                <option key={t.key} value={t.key}>{t.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="space-y-2">
                    {isLoading && <p className="text-center py-6 text-base text-gray-400">불러오는 중...</p>}
                    {!isLoading && reviews.map((rv) => {
                        const st = STATUS_META[rv.review_status] ?? STATUS_META.OPEN;
                        return (
                            <div
                                key={rv.id}
                                onClick={() => setDetailReview({
                                    rv,
                                    evalItem: needsReview.find((ev) => ev.id === rv.evaluation_result_id) ?? null,
                                })}
                                className="p-3 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors space-y-1.5"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className={`text-sm px-1.5 py-0.5 rounded ${st.bg} ${st.color} font-medium`}>{st.label}</span>
                                        <span className="text-sm px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">
                                            {ISSUE_TYPES.find((t) => t.key === rv.issue_type)?.label ?? rv.issue_type}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-gray-400">{rv.created_at?.slice(0, 10)}</span>
                                        {rv.review_status !== 'CLOSED' && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleComplete(rv); }}
                                                className="text-sm px-2 py-0.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors flex items-center gap-1"
                                            >
                                                <CheckCircle2 size={10} /> 완료 처리
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <p className="text-base font-medium text-sb-header">{rv.problem_summary || '—'}</p>
                                <div className="flex items-center gap-2 text-sm text-gray-400">
                                    <span>검토자: <span className="text-gray-600">{rv.reviewer_id || '—'}</span></span>
                                    {rv.action_required === 'Y' && (
                                        <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-600">조치 필요</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    {!isLoading && reviews.length === 0 && (
                        <p className="text-center py-6 text-base text-gray-400">
                            {filterStatus !== 'all' || filterIssueType
                                ? '선택한 조건에 맞는 검토 이력이 없습니다.'
                                : '아직 등록된 검토 의견이 없습니다. 위 목록에서 항목을 선택해 검토하기 버튼을 눌러보세요.'}
                        </p>
                    )}
                </div>
                <Pagination
                    currentPage={reviewParams.page}
                    totalPages={totalPages}
                    onPageChange={(p) => setReviewParams((prev) => ({ ...prev, page: p }))}
                />
            </div>

            {/* ── 검토 상세 모달 ── */}
            {detailReview && (
                <ReviewDetailModal
                    reviewId={detailReview.rv.id}
                    evalItem={detailReview.evalItem}
                    onClose={() => setDetailReview(null)}
                    onComplete={(rv) => { handleComplete(rv); setDetailReview(null); }}
                    onRegisterPolicy={handleRegisterPolicy}
                />
            )}

            {/* ── 검토 입력 모달 ── */}
            <Modal show={showForm} centered onHide={() => setShowForm(false)} contentClassName="rounded-2xl border-0 shadow-xl">
                <Modal.Header className="border-b border-gray-100 px-6 py-3">
                    <Modal.Title className="text-base font-bold text-sb-header">수정 의견 등록</Modal.Title>
                    <button type="button" className="modal-btn-close" onClick={() => setShowForm(false)}>
                        <span className="modal-btn-span">X</span>
                    </button>
                </Modal.Header>
                <Modal.Body className="px-6 py-4 space-y-3">
                    {editReview && (
                        <>
                            <div className="p-3 rounded-lg bg-gray-50 flex items-center gap-2">
                                <span className={`text-sm px-2 py-0.5 rounded-full font-medium ${getVBg(editReview.grade)} ${getVText(editReview.grade)}`}>
                                    {{ FAIL: '개선 필요', WARN: '주의', PASS: '정상' }[editReview.grade] ?? editReview.grade}
                                </span>
                                <p className="text-base text-gray-700 truncate flex-1">{editReview.question}</p>
                                <span className={`text-base font-bold ${getScoreColor(editReview.final_score)}`}>{editReview.final_score}점</span>
                            </div>
                            <div>
                                <label className="text-sm text-gray-500 block mb-1">어떤 문제인가요?</label>
                                <select
                                    value={editReview.issue_type}
                                    onChange={(e) => setEditReview({ ...editReview, issue_type: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                                >
                                    {ISSUE_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-sm text-gray-500 block mb-1">문제 요약 <span className="text-red-400">*</span></label>
                                <textarea
                                    value={editReview.problem_summary}
                                    onChange={(e) => setEditReview({ ...editReview, problem_summary: e.target.value })}
                                    rows={2}
                                    className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary resize-y"
                                    placeholder="예: 제29조 교육 시간이 8시간인데 6시간으로 답변함"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-gray-500 block mb-1">상세 의견 <span className="text-gray-400 font-normal">(선택)</span></label>
                                <textarea
                                    value={editReview.review_comment}
                                    onChange={(e) => setEditReview({ ...editReview, review_comment: e.target.value })}
                                    rows={3}
                                    className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary resize-y"
                                    placeholder="어떤 내용이 누락되었는지, 무엇이 잘못되었는지 구체적으로 적어주세요"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-gray-500 block mb-1">개선 방향 <span className="text-gray-400 font-normal">(선택 — 비슷한 질문 전체에 적용할 규칙)</span></label>
                                <textarea
                                    value={editReview.suggested_policy_text}
                                    onChange={(e) => setEditReview({ ...editReview, suggested_policy_text: e.target.value })}
                                    rows={2}
                                    className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary resize-y"
                                    placeholder="예: 교육 관련 질문에는 반드시 제29조를 우선 검색하도록 한다"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-gray-500 block mb-1">검토자</label>
                                <span className="flex items-center px-3 py-2 rounded-lg text-base border border-gray-100 bg-gray-50 text-gray-600">
                                    {editReview.reviewer_id || '로그인 필요'}
                                </span>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
                                <label className="text-sm text-amber-800 flex-1">지금 바로 조치가 필요한 심각한 오류인가요?</label>
                                <select
                                    value={editReview.action_required}
                                    onChange={(e) => setEditReview({ ...editReview, action_required: e.target.value })}
                                    className="px-2 py-1 rounded text-sm border border-amber-200 bg-white outline-none focus:border-sb-primary"
                                >
                                    <option value="N">아니오 (일반 개선)</option>
                                    <option value="Y">예 (즉시 조치 필요)</option>
                                </select>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 px-6 pb-4 pt-0">
                    <Button variant="light" className="text-gray-600" onClick={() => setShowForm(false)}>취소</Button>
                    <Button variant="primary" className="bg-sb-primary border-0" onClick={handleSave} disabled={create.isLoading}>
                        {create.isLoading ? '등록 중...' : '등록'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
